/**
 * Open Orders — the optimistic row and the real one, in one list.
 *
 * ## The 60 % opacity row is a specification, not a flourish
 *
 * components.md §4.5: "a locally submitted order renders immediately at 60 %
 * opacity with a `PENDING` pill until the WS `order` ack arrives; on reject it
 * flashes `danger` and removes itself with a toast. Note the taker delay
 * (D12): a marketable order legitimately sits `PENDING` for the hold and the
 * round trip.
 * **The pill must therefore appear instantly and read as normal, not as a
 * warning.**"
 *
 * So: no spinner, no colour change, no toast on the way in. The row is there,
 * it is dimmer, it says `PENDING`, and a quarter of a second later it is a
 * normal row. A user who has just clicked Buy should see their order, not a
 * progress indicator for a delay the venue publishes in `/config`.
 *
 * ## Rows enter at the top and never re-sort
 *
 * components.md §3, rule 1: a new row appears every sixty seconds, and if a sort
 * key could change under a pointer the user cancels the wrong order. The order
 * is fixed — pending first, then by `created_at_ms` descending — and computed in
 * one `useMemo` rather than by the table, which takes rows in final order and
 * never sorts.
 */

import { memo, useMemo, useState } from 'react'
import { Button, EmptyState, Glyph, Pill, Table } from '@/components/primitives'
import type { TableColumn } from '@/components/primitives'
import { cx } from '@/lib/cx'
import { formatAvgCents, formatCents, formatShares } from '@/lib/format'
import type { PendingOrder } from '@/stores/pending-orders'
import { useOrders, usePendingOrders, useRuntime } from '@/app/use-runtime'
import { RESTING_STATUSES } from '@/domain/pro-ladder'

/** A row is either the server's order or this tab's optimistic stand-in. */
type Row =
  | { kind: 'pending'; key: string; sortMs: number; pending: PendingOrder }
  | { kind: 'order'; key: string; sortMs: number; order: Record<string, unknown> }

/** One definition, shared with the ticket and the pro ladder — three surfaces
 *  that must never disagree about whether an order is on the book. */
const RESTING = RESTING_STATUSES

export interface OpenOrdersTableProps {
  /** Filters to one market. `null` shows every market, which is the default —
   *  a user holds BTC and ETH at the same anchor routinely (components.md §3). */
  marketId?: string | null
  className?: string
}

export const OpenOrdersTable = memo(function OpenOrdersTable({ marketId, className }: OpenOrdersTableProps) {
  const runtime = useRuntime()
  const { rows: orders } = useOrders<Record<string, unknown>>()
  const { rows: pending } = usePendingOrders()
  const [cancelling, setCancelling] = useState<string | null>(null)

  const rows = useMemo<Row[]>(() => {
    const out: Row[] = []
    for (const p of pending) {
      if (marketId && p.marketId !== marketId) continue
      out.push({ kind: 'pending', key: `p:${p.clientOrderId}`, sortMs: p.createdAtMs, pending: p })
    }
    for (const o of orders) {
      if (!RESTING.has(String(o.status))) continue
      if (marketId && o.market_id !== marketId) continue
      out.push({
        kind: 'order',
        key: `o:${String(o.order_id)}`,
        sortMs: typeof o.created_at_ms === 'number' ? o.created_at_ms : 0,
        order: o,
      })
    }
    // Newest first, and stable: two orders created in the same millisecond keep
    // their insertion order rather than swapping under the pointer.
    return out.sort((a, b) => b.sortMs - a.sortMs)
  }, [orders, pending, marketId])

  const columns = useMemo<TableColumn<Row>[]>(
    () => [
      {
        id: 'market',
        header: 'Market',
        card: 'title',
        cell: (row) => {
          const id = row.kind === 'pending' ? row.pending.marketId : String(row.order.market_id)
          return (
            <span className="truncate text-body-sm" title={id}>
              {id}
            </span>
          )
        },
      },
      {
        id: 'outcome',
        header: 'Outcome',
        card: 'title',
        cell: (row) => {
          const outcome = row.kind === 'pending' ? row.pending.outcome : String(row.order.outcome)
          return (
            <span className={cx('flex items-center gap-1', outcome === 'UP' ? 'text-up-text' : 'text-down-text')}>
              <Glyph name={outcome === 'UP' ? 'up' : 'down'} label={null} />
              {outcome}
            </span>
          )
        },
      },
      {
        id: 'side',
        header: 'Side',
        card: 'title',
        cell: (row) => (row.kind === 'pending' ? row.pending.side : String(row.order.side)),
      },
      {
        id: 'price',
        header: 'Price',
        numeric: true,
        cell: (row) => {
          const cents = row.kind === 'pending' ? row.pending.priceCents : row.order.price_cents
          return typeof cents === 'number' ? formatCents(cents) : 'MKT'
        },
      },
      {
        id: 'size',
        header: 'Size',
        numeric: true,
        cell: (row) => formatShares(row.kind === 'pending' ? row.pending.size : String(row.order.size ?? '0')),
      },
      {
        id: 'filled',
        header: 'Filled',
        numeric: true,
        cell: (row) =>
          row.kind === 'pending' ? '—' : (
            <span className="ud-num">
              {formatShares(String(row.order.filled_size ?? '0'))}
              {typeof row.order.avg_fill_price_cents === 'string' && hasFill(row.order) ?
                <span className="text-text-tertiary"> @ {formatAvgCents(row.order.avg_fill_price_cents)}</span>
              : null}
            </span>
          ),
      },
      {
        id: 'status',
        header: 'Status',
        card: 'badge',
        cell: (row) =>
          row.kind === 'pending' ? (
            // No spinner and no warning tone. The taker-delay hold is normal (D12).
            <Pill tone="neutral">PENDING</Pill>
          ) : (
            <Pill tone={String(row.order.status) === 'PARTIALLY_FILLED' ? 'info' : 'neutral'}>
              {String(row.order.status)}
            </Pill>
          ),
      },
      {
        id: 'cancel',
        header: '',
        card: 'action',
        width: 'max-content',
        cell: (row) =>
          row.kind === 'order' ? (
            <Button
              size="sm"
              variant="ghost"
              loading={cancelling === String(row.order.order_id)}
              onClick={() => {
                const id = String(row.order.order_id)
                setCancelling(id)
                void runtime.trading.cancel(id).finally(() => setCancelling(null))
              }}
            >
              Cancel
            </Button>
          ) : (
            // A pending order has no `order_id` yet, so there is nothing to
            // address a `DELETE` to. Cancelling during the hold *is* allowed
            // (§7.5) — the moment the receipt lands, this cell becomes a button.
            <span className="text-caption text-text-tertiary">—</span>
          ),
      },
    ],
    [cancelling, runtime],
  )

  return (
    <div className={cx('flex min-h-0 flex-col', className)}>
      <Table
        label="open orders"
        columns={columns}
        rows={rows}
        rowKey={(row) => row.key}
        empty={<EmptyState>No resting orders.</EmptyState>}
        // The optimistic row is dimmed here rather than inside every cell, so a
        // cell cannot forget. `data-pending` is what the browser suite asserts on.
        rowClassName={(row) => (row.kind === 'pending' ? 'opacity-60' : undefined)}
        rowAttributes={(row) => (row.kind === 'pending' ? { 'data-pending': 'true' } : {})}
      />
      {rows.length > 0 ? (
        <div className="flex justify-end border-t border-border-subtle p-1_5">
          <Button size="sm" variant="secondary" onClick={() => void runtime.trading.cancelAll(marketId ?? undefined)}>
            Cancel all
          </Button>
        </div>
      ) : null}
    </div>
  )
})

/** Whether an order has any fill at all, without a float on a share count. */
function hasFill(order: Record<string, unknown>): boolean {
  return /[1-9]/.test(String(order.filled_size ?? '0'))
}
